import {
  collection,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { dilemmaDocumentConverter } from "@kekkle/shared/convertors/web/dilemma-document.converter";
import type { DilemmaDocument } from "@kekkle/shared/types/documents/dilemma-document/dilemma-document";
import { handleFirestoreException } from "@/helpers/exceptions/handler";

/**
 * Returns a reference to the dilemmas collection for a specific group with converter
 */
function dilemmasCollection(
  groupId: string,
): CollectionReference<DilemmaDocument> {
  return collection(firestore, `groups/${groupId}/dilemmas`).withConverter(
    dilemmaDocumentConverter,
  );
}

/**
 * Subscribes to all dilemmas for a group, ordered by created_at descending.
 */
export function subscribeToDilemmas(
  groupId: string,
  callbacks: {
    onDilemmas: (dilemmas: DilemmaDocument[]) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  const q = query(dilemmasCollection(groupId), orderBy("created_at", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      callbacks.onDilemmas(snapshot.docs.map((doc) => doc.data()));
    },
    (error) => {
      callbacks.onError(error);
    },
  );
}

/**
 * Adds a new dilemma to the group
 */
export async function addDilemma(
  groupId: string,
  data: Omit<DilemmaDocument, "_id">,
): Promise<void> {
  const batch = writeBatch(firestore);
  const dilemmaColl = dilemmasCollection(groupId);
  const newDilemmaRef = doc(dilemmaColl);
  batch.set(newDilemmaRef, data);
  // (Optional: If we tracked number_of_dilemmas in group_data we would update it here)
  return await batch.commit();
}

/**
 * Cast a vote on a dilemma
 */
export async function voteOnDilemma(
  groupId: string,
  dilemmaId: string,
  userId: string,
  selectedOption: string,
  isAnonymous: boolean,
): Promise<void> {
  try {
    const dilemmaRef = doc(dilemmasCollection(groupId), dilemmaId);
    if (isAnonymous) {
      await updateDoc(dilemmaRef, {
        participants: arrayUnion(userId),
        [`anonymous_results.${selectedOption}`]: increment(1),
      });
    } else {
      await updateDoc(dilemmaRef, {
        participants: arrayUnion(userId),
        [`answers.${userId}`]: selectedOption,
      });
    }
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Change vote on a non-anonymous dilemma
 */
export async function revoteOnDilemma(
  groupId: string,
  dilemmaId: string,
  userId: string,
  newSelectedOption: string,
): Promise<void> {
  try {
    const dilemmaRef = doc(dilemmasCollection(groupId), dilemmaId);
    await updateDoc(dilemmaRef, {
      [`answers.${userId}`]: newSelectedOption,
    });
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Deletes a dilemma from the group
 */
export async function deleteDilemma(
  groupId: string,
  dilemmaId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const dilemmaRef = doc(dilemmasCollection(groupId), dilemmaId);
    batch.delete(dilemmaRef);
    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}
